<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.root"
  >
    <div :style="style.container">
      <div :style="style.headerContainer">
        <HeaderLargePrimary>
          {{ survey.name }}
        </HeaderLargePrimary>
      </div>
      <TextRegular :style="style.headline">
        Select add all the skills you have.
      </TextRegular>
      <FlexLayoutPrimary :showFooter="true">
        <template v-slot:left>
          <TextCardLabelPrimary :style="style.cardLabel">
            CATEGORIES
          </TextCardLabelPrimary>
          <CardPrimary :style="style.cardSurveyCategories">
            <RadioListPrimary
              v-model="selectedSurveyCategoryId"
              :disableDeselect="true"
              :items="surveyCategories"
              :labelKey="'name'"
              :truncate="true"
            />
          </CardPrimary>
        </template>
        <template v-slot:center>
          <TextCardLabelPrimary :style="style.cardLabel">
            SKILLS
          </TextCardLabelPrimary>
          <CardPrimary :style="style.cardSurveyCategorySkills">
            <RadioListPrimary
              v-model="selectedSurveyCategorySkillId"
              :disableDeselect="true"
              :items="selectedSurveyCategorySkills"
              :labelKey="'name'"
              :truncate="true"
            >
              <template v-slot:item="props">
                <div :style="style.surveyCategorySkillItemContainer">
                  <TextRegular
                    :inheritFontStyle="true"
                    :truncate="true"
                  >
                    {{ props.label }}
                  </TextRegular>
                  <GoogleMaterialIconPrimary
                    v-if="isOwned(props.item.skill.id)"
                    :content="'check'"
                    :style="style.iconCheck"
                  />
                </div>
              </template>
            </RadioListPrimary>
          </CardPrimary>
        </template>
        <template v-slot:right>
          <SkillDetail
            :isPreview="false"
            :ownedSkillIds="ownedSkillIds"
            :survey="survey"
            :surveyCategorySkill="selectedSurveyCategorySkill"
            :surveyCategoryTenantUserSkills="surveyCategoryTenantUserSkills"
            :tenantUserSkills="unlistedTenantUserSkills"
            @add="skillName => openModalToCreateTenantUserSkill(skillName)"
            @edit="openModalToEditTenantUserSkill"
            @select-tenant-user-skill="onSelectTenantUserSkill"
            @unselect-tenant-user-skill="onUnselectTenantUserSkill"
          />
        </template>
      </FlexLayoutPrimary>
    </div>
    <div :style="style.footer">
      <template v-if="surveyIsCompleted">
        <div :style="style.footerLeft">
          <HeaderMediumSecondary>
            You have already completed this survey.
          </HeaderMediumSecondary>
          <TextMutedSecondary :style="style.footerTextMuted">
            You can still add your skills from this page.
          </TextMutedSecondary>
        </div>
        <ButtonSecondary
          @click="goToHomePage"
        >
          Return to Home Page
        </ButtonSecondary>
      </template>
      <template v-else>
        <HeaderMediumSecondary :style="style.footerHeader">
          Have you selected all the skills you have?
        </HeaderMediumSecondary>
        <div :style="style.footerRight">
          <TextButtonMuted
            @click="pendSurvey"
            :style="style.footerButton"
          >
            Finish Later
          </TextButtonMuted>
          <ButtonPrimary
            @click="completeSurvey"
          >
            Complete Survey
          </ButtonPrimary>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import ButtonPrimary from '@modules/v2/button/button-primary'
  import ButtonSecondary from '@modules/v2/button/button-secondary'
  import CardPrimary from '@modules/v2/card/card-primary'
  import FlexLayoutPrimary from '@survey/modules/flex-layout-primary'
  import GoogleMaterialIconPrimary
    from '@modules/v2/icon/google-material-icon-primary'
  import HeaderLargePrimary from '@modules/v2/header/header-large-primary'
  import HeaderMediumSecondary from '@modules/v2/header/header-medium-secondary'
  import RadioListPrimary from '@modules/v2/radio-list/radio-list-primary'
  import SkillDetail from '@survey/modules/skill-detail'
  import TextButtonMuted from '@modules/v2/button/text-button-muted'
  import TextCardLabelPrimary from '@modules/v2/text/text-card-label-primary'
  import TextMutedSecondary from '@modules/v2/text/text-muted-secondary'
  import TextRegular from '@modules/v2/text/text-regular'
  import { DIALOG_NAMES } from '@js/constants'
  import { Layout } from '@js/styles'
  import { mapGetters } from 'vuex'
  import { MODAL_WINDOW_NAMES } from '@js/constants'

  export default {
    name: 'SurveyView',
    inheritAttrs: false,
    components: {
      ButtonPrimary,
      ButtonSecondary,
      CardPrimary,
      FlexLayoutPrimary,
      GoogleMaterialIconPrimary,
      HeaderLargePrimary,
      HeaderMediumSecondary,
      RadioListPrimary,
      SkillDetail,
      TextButtonMuted,
      TextCardLabelPrimary,
      TextMutedSecondary,
      TextRegular,
    },
    props: {
      guidedMode: {
        type: Boolean,
        reuqired: false,
        default: false,
      },
      survey: {
        type: Object,
        required: false,
        default: () => {},
      },
      surveyCategorySkills: {
        type: Array,
        required: false,
        default: () => [],
      },
      surveyCategoryTenantUserSkills: {
        type: Array,
        required: false,
        default: () => [],
      },
      surveyState: {
        type: Object,
        required: false,
        default: () => {},
      },
      tenantUser: {
        type: Object,
        required: false,
        default: () => {},
      },
      tenantUserSkills: { // Passed from root view.
        type: Array,
        required: false,
        default: () => [],
      },
    },
    data: function () {
      return {
        selectedSurveyCategoryId: null,
        selectedSurveyCategorySkillId: null,
        style: {
          cardLabel: {
            height: Layout.space.md,
            margin: `0 0 ${Layout.space.md} 0`,
          },
          cardSurveyCategories: {
            height: 'calc(100% - 32px)',
            overflow: 'scroll',
            padding: Layout.space.sm,
            width: '100%',
          },
          cardSurveyCategorySkills: {
            height: 'calc(100% - 32px)',
            overflow: 'scroll',
            padding: Layout.space.sm,
            width: '100%',
          },
          container: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            margin: `${Layout.space.lg} auto ${Layout.space.sm}`,
            maxWidth: Layout.container.primary.maxWidth,
            width: '100%',
          },
          footer: {
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            height: '80px',
            justifyContent: 'space-between',
            padding: '0 30px',
            width: '100%',
          },
          footerButton: {
            margin: `0 ${Layout.space.md}`,
          },
          footerDummy: {
            border: 'solid 1px blue',
            flex: 1,
            height: '1px',
            justifyContent: 'flex-end',
          },
          footerHeader: {
            flex: 1,
            margin: `${Layout.space.sm}`,
            minWidth: '200px',
          },
          footerLeft: {
            display: 'flex',
            flexDirection: 'column',
          },
          footerRight: {
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            height: 'fit-content',
            justifyContent: 'flex-end',
            margin: `${Layout.space.sm}`,
          },
          footerTextMuted: {
            margin: `${Layout.space.xs} 0 0 0`,
          },
          headerContainer: {
            alignItems: 'center',
            display: 'flex',
            height: '30px',
            justifyContent: 'space-between',
            padding: Layout.container.md.padding,
            width: '100%',
          },
          headline: {
            padding: `${Layout.space.lg} ${Layout.space.lg}`,
          },
          iconCheck: {
            margin: `0 0 0 ${Layout.space.xs}`,
          },
          innerContainer: {
            width: '100%',
          },
          root: {
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'flex-start',
            width: '100%',
          },
          surveyCategorySkillItemContainer: {
            alignItems: 'center',
            display: 'flex',
            width: '100%',
          },
        },
      };
    },
    created: async function () {
      Object.assign(
        this.style.footer,
        {
          backgroundColor: this.color.light1(),
        },
      );
    },
    mounted: async function () {
      if (this.surveyCategories.length > 0) {
        this.selectedSurveyCategoryId = this.surveyCategories[0].id;
      }

      if (this.selectedSurveyCategorySkills.length > 0) {
        this.selectedSurveyCategorySkillId = this.selectedSurveyCategorySkills[0].id;
      }
    },
    computed: {
      ...mapGetters('colorTheme', ['color']),
      ...mapGetters('globalVariables', ['getSurveyStateCompleted']),
      descriptionOfOthers: function () {
        return (
          'If you have any other skills related to ' +
          `"${this.selectedSurveyCategory.name}", please ` +
          (`${this.tenantUserSkills.length > 0 ? 'select them or ' : ''}`) +
          'add them as new skills.'
        );
      },
      ownedSkillIds: function () {
        return this.tenantUserSkills.map(elem => elem?.skill?.id);
      },
      selectedSkillId: function () {
        if (this.selectedSurveyCategorySkillId === null) {
          return null;
        }

        return this.selectedSurveyCategorySkill.skill?.id;
      },
      selectedSurveyCategory: function () {
        if (this.selectedSurveyCategoryId === null) {
          return {};
        }
        const index = this.surveyCategories
          .findIndex(elem => elem.id === this.selectedSurveyCategoryId);
        return index >= 0 ? this.surveyCategories[index] : {};
      },
      selectedSurveyCategorySkill: function () {
        if (this.selectedSurveyCategorySkillId === null) {
          return {};
        }
        return this.selectedSurveyCategorySkills
          .filter(elem => elem?.id === this.selectedSurveyCategorySkillId)[0];
      },
      selectedSurveyCategorySkills: function () {
        if (this.selectedSurveyCategoryId === null) {
          return [];
        }

        return this.surveyCategorySkills
          .filter(
            elem => elem?.surveyCategory?.id === this.selectedSurveyCategoryId
          )
          .map(elem => {
            return {
              ...elem,
              name: elem?.skill?.name,
             };
          })
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .concat(
            {
              id: -1,
              name: 'Others',
              surveyCategory: this.selectedSurveyCategory,
              skill: {
                id: null,
                name: 'Others',
              },
              description: this.descriptionOfOthers,
            },
          );
      },
      selectedTenantUserSkill: function () {
        if (this.selectedSurveyCategorySkillId === null) {
          return null;
        }

        return this.tenantUserSkills
          .filter(elem => elem.skill?.id === this.selectedSkillId)[0];
      },
      surveyCategories: function () {
        return this.surveyCategorySkills
          .map(elem => elem.surveyCategory)
          .filter(
            (elem, index, array) => {
              return array.findIndex(item => item.id === elem.id) === index
            }
          )
          .sort((a, b) => a.displayOrder - b.displayOrder);
      },
      surveyCategorySkillIds: function () {
        return this.surveyCategorySkills.map(elem => elem.skill.id);
      },
      surveyIsCompleted: function () {
        return this.surveyState.stateType === this.getSurveyStateCompleted;
      },
      unlistedTenantUserSkills: function () {
        const skillIds = this.surveyCategorySkills.map(elem => elem.skill.id);
        return this.tenantUserSkills.filter(
          elem => !skillIds.includes(elem.skill.id)
        );
      },
    },
    methods: {
      completeSurvey: async function () {
        const params = {
          survey: this.survey,
        };
        this.$modal.show(DIALOG_NAMES.COMPLETE_SURVEY_PRIMARY, params);
      },
      goToHomePage: function () {
        this.$router.push({
          name: 'private-home',
        });
      },
      isOwned: function (skillId) {
        return (
          (
            skillId &&
            this.ownedSkillIds.includes(skillId)
          ) ||
          (
            !skillId &&
            this.surveyCategoryTenantUserSkills
              .filter(
                elem => {
                  return !this.surveyCategorySkillIds.includes(
                    elem.tenantUserSkill.skill.id
                  );
                }
              )
              .map(elem => elem.surveyCategory.id)
              .includes(this.selectedSurveyCategory.id)
          )
        );
      },
      onSelectTenantUserSkill: function (tenantUserSkill) {
        this.$emit(
          'create-survey-category-tenant-user-skill',
          this.survey.id,
          this.selectedSurveyCategory.id,
          tenantUserSkill.id,
          tenantUserSkill.skill.id,
        );
      },
      onUnselectTenantUserSkill: function (tenantUserSkill) {
        this.$emit(
          'delete-survey-category-tenant-user-skill',
          this.survey.id,
          this.selectedSurveyCategory.id,
          tenantUserSkill.id,
        );
      },
      openModalToCreateTenantUserSkill: function (skillName) {
        const params = {
          guidedMode: this.guidedMode,
          skillName: skillName,
          tenantUser: this.tenantUser,
          tenantUserSkill: null,
          tenantUserSkills: this.tenantUserSkills,
          payload: skillName
            ? null
            : {
              surveyId: this.survey.id,
              surveyCategoryId: this.selectedSurveyCategoryId,
            },
        };

        this.$modal.show(
          MODAL_WINDOW_NAMES.CONFIGURE_TENANT_USER_SKILL_PRIMARY,
          params,
        );
      },
      openModalToEditTenantUserSkill: function () {
        const params = {
          guidedMode: false,
          skillName: this.selectedTenantUserSkill?.skill?.name,
          tenantUser: this.tenantUser,
          tenantUserSkill: this.selectedTenantUserSkill,
          tenantUserSkills: this.tenantUserSkills,
        };

        this.$modal.show(
          MODAL_WINDOW_NAMES.CONFIGURE_TENANT_USER_SKILL_PRIMARY,
          params,
        );
      },
      pendSurvey: async function () {
        this.$store.commit(
          'notification/setMessage',
          `Survey (${this.survey.name}) is on hold`,
        );
        this.$router.push({
          name: 'private-home',
        });
      },
    },
    watch: {
      selectedSurveyCategoryId: function (newVal, oldVal) {
        this.selectedSurveyCategorySkillId = (
          this.selectedSurveyCategorySkills.length > 0
            ? this.selectedSurveyCategorySkills[0].id
            : null
        );
      },
    },
  };
</script>
